import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import type { Profile, UserRole } from '@/context/AuthContext';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { getFreshToken } from '@/lib/auth-token';
import UserFormModal from './components/UserFormModal';
import DeleteUserModal from './components/DeleteUserModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import SettingsTab from './components/SettingsTab';
import SettingsFormacaoTab from './components/SettingsFormacaoTab';
import UnidadesTab from './components/UnidadesTab';
import AlteracoesTab from './components/AlteracoesTab';
import AppearanceTab from './components/AppearanceTab';
import AppearanceFormacaoTab from './components/AppearanceFormacaoTab';
import AdminStatsBar from './components/AdminStatsBar';
import DiagnosticoTab from './components/DiagnosticoTab';
import MunicipiosTab from './components/MunicipiosTab';
import CarreirasTab from './components/CarreirasTab';
import LogsTab from './components/LogsTab';
import CronJobsTab from './components/CronJobsTab';
import EmailTemplatesTab from './components/EmailTemplatesTab';
import FeriadosTab from './components/FeriadosTab';
import DadosMestreTab from './components/DadosMestreTab';

type Tab = 'utilizadores' | 'municipios' | 'unidades' | 'carreiras' | 'alteracoes' | 'configuracoes' | 'configuracoes_formacao' | 'aparencia' | 'aparencia_formacao' | 'diagnostico' | 'logs' | 'tarefas' | 'templates' | 'feriados' | 'dados_mestre';

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-manage-users`;

const ALL_TABS: { id: Tab; label: string; icon: string; modulo: 'siadap' | 'formacao' | 'ambos' }[] = [
  { id: 'utilizadores',          label: 'Utilizadores',         icon: 'ri-team-line',          modulo: 'ambos' },
  { id: 'municipios',            label: 'Municípios',           icon: 'ri-building-4-line',    modulo: 'siadap' },
  { id: 'unidades',              label: 'Unidades Orgânicas',   icon: 'ri-building-2-line',    modulo: 'ambos' },
  { id: 'carreiras',             label: 'Carreiras',            icon: 'ri-briefcase-line',     modulo: 'ambos' },
  { id: 'alteracoes',            label: 'Alterações',           icon: 'ri-file-list-3-line',   modulo: 'siadap' },
  { id: 'configuracoes',         label: 'Config. SIADAP',       icon: 'ri-settings-3-line',    modulo: 'siadap' },
  { id: 'configuracoes_formacao', label: 'Config. Formação',    icon: 'ri-book-open-line',     modulo: 'formacao' },
  { id: 'aparencia',             label: 'Aparência SIADAP',      icon: 'ri-paint-brush-line',   modulo: 'siadap' },
  { id: 'aparencia_formacao',    label: 'Aparência Formação',    icon: 'ri-palette-line',       modulo: 'formacao' },
  { id: 'tarefas',               label: 'Tarefas Automáticas',   icon: 'ri-time-line',          modulo: 'ambos' },
  { id: 'templates',             label: 'Templates Email',       icon: 'ri-mail-settings-line', modulo: 'ambos' },
  { id: 'feriados',              label: 'Feriados',              icon: 'ri-calendar-event-line',modulo: 'ambos' },
  { id: 'dados_mestre',          label: 'Dados Mestre',          icon: 'ri-database-2-line',    modulo: 'formacao' },
  { id: 'diagnostico',           label: 'Diagnóstico',          icon: 'ri-pulse-line',         modulo: 'ambos' },
  { id: 'logs',                  label: 'Logs de Atividade',    icon: 'ri-fingerprint-line',   modulo: 'ambos' },
];


export default function AdminPage() {
  const { profile, isSuperAdmin } = useAuth();
  const { activeMunicipio, municipios } = useActiveMunicipio();

  // Determinar o módulo ativo com base no município selecionado
  const isFormacaoModule =
    activeMunicipio?.sigla === 'GF' ||
    activeMunicipio?.nome === 'Gestão da Formação';

  // Filtrar tabs visíveis pelo módulo ativo
  const visibleTabs = ALL_TABS.filter(
    (t) => t.modulo === 'ambos' || (isFormacaoModule ? t.modulo === 'formacao' : t.modulo === 'siadap')
  );

  const [tab, setTab] = useState<Tab>('utilizadores');

  // Quando o módulo muda, ajustar o tab se o atual não for visível
  useEffect(() => {
    const validIds = new Set(visibleTabs.map((t) => t.id));
    if (!validIds.has(tab)) {
      setTab('utilizadores');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormacaoModule]);

  const [users, setUsers] = useState<Profile[]>([]);
  const [allUsers, setAllUsers] = useState<Profile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState<Profile | null>(null);
  const [deleteUser, setDeleteUser] = useState<Profile | null>(null);
  const [changePassUser, setChangePassUser] = useState<Profile | null>(null);
  const [userMsg, setUserMsg] = useState('');
  const [userError, setUserError] = useState('');
  const [filtroModulo, setFiltroModulo] = useState<string>('todos');
  const [pesquisa, setPesquisa] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const token = await getFreshToken();
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'list_users' }),
      });
      const json = await res.json();
      const fetched: Profile[] = json.users ?? [];
      setAllUsers(fetched);
      if (activeMunicipio && municipios.length > 1) {
        setUsers(fetched.filter((u) => u.municipio_id === activeMunicipio.id || u.role === 'super_admin'));
      } else {
        setUsers(fetched);
      }
    } catch {
      setUsers([]);
      setAllUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [activeMunicipio, municipios.length]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const callEdge = async (body: object) => {
    const token = await getFreshToken();
    const res = await fetch(EDGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error ?? `Erro HTTP ${res.status}`);
    return json;
  };

  const handleCreate = async (data: { email: string; full_name: string; role: UserRole; password?: string; municipio_id?: string }) => {
    setUserError('');
    try {
      await callEdge({ action: 'create', ...data });
      setShowCreate(false);
      setUserMsg('Utilizador criado com sucesso!');
      await fetchUsers();
      setTimeout(() => setUserMsg(''), 4000);
    } catch (err: unknown) { throw err; }
  };

  const handleEdit = async (data: { email: string; full_name: string; role: UserRole; municipio_id?: string }) => {
    if (!editUser) return;
    setUserError('');
    try {
      await callEdge({ action: 'update_role', userId: editUser.id, full_name: data.full_name, role: data.role, municipio_id: data.municipio_id });
      setEditUser(null);
      setUserMsg('Utilizador atualizado!');
      await fetchUsers();
      setTimeout(() => setUserMsg(''), 4000);
    } catch (err: unknown) { throw err; }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setUserError('');
    try {
      await callEdge({ action: 'delete', userId: deleteUser.id });
      setDeleteUser(null);
      setUserMsg('Utilizador removido.');
      await fetchUsers();
      setTimeout(() => setUserMsg(''), 4000);
    } catch (err: unknown) {
      setDeleteUser(null);
      setUserError(err instanceof Error ? err.message : 'Erro ao remover utilizador.');
      setTimeout(() => setUserError(''), 6000);
    }
  };

  const handleChangePassword = async (password: string) => {
    if (!changePassUser) return;
    setUserError('');
    try {
      await callEdge({ action: 'change_password', userId: changePassUser.id, password });
      setChangePassUser(null);
      setUserMsg('Password alterada com sucesso!');
      setTimeout(() => setUserMsg(''), 4000);
    } catch (err: unknown) { throw err; }
  };

  const formatLastAccess = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Nunca';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours}h`;
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `Há ${diffDays} dias`;
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const roleLabel: Record<string, string> = {
    super_admin: 'Super Admin',
    admin: 'Administrador',
    viewer: 'Visualizador',
  };

  const roleBadge: Record<string, string> = {
    super_admin: 'bg-zinc-900 text-white',
    admin: 'bg-amber-100 text-amber-800',
    viewer: 'bg-zinc-100 text-zinc-600',
  };

  return (
    <div className="flex-1 min-h-screen bg-[#f5f5f3]">

      {/* Page header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-shield-keyhole-line text-white text-lg" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Área Restrita</span>
                </div>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight">Administração</h1>
                <p className="text-[12px] text-zinc-400 mt-0.5">
                  {isFormacaoModule
                    ? 'Gestão de utilizadores, unidades orgânicas e configurações da Gestão da Formação'
                    : 'Gestão de utilizadores, unidades orgânicas e configurações do SIADAP'}
                </p>
              </div>
            </div>
            {/* Badge módulo ativo */}
            {activeMunicipio && municipios.length > 1 && (
              <div className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl flex-shrink-0 ${
                isFormacaoModule ? 'bg-emerald-50 border-emerald-200' : 'bg-zinc-50 border-zinc-200'
              }`}>
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${
                  isFormacaoModule ? 'bg-emerald-600' : 'bg-zinc-900'
                }`}>
                  <span className="text-[9px] font-bold text-white leading-none">
                    {activeMunicipio.sigla?.slice(0, 2) ?? '?'}
                  </span>
                </div>
                <div>
                  <p className={`text-[10px] font-semibold uppercase tracking-widest leading-none mb-0.5 ${
                    isFormacaoModule ? 'text-emerald-500' : 'text-zinc-400'
                  }`}>Módulo ativo</p>
                  <p className={`text-[13px] font-semibold leading-none ${
                    isFormacaoModule ? 'text-emerald-800' : 'text-zinc-800'
                  }`}>{activeMunicipio.nome}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">

        {/* Stats rápidas */}
        <AdminStatsBar users={users} loadingUsers={loadingUsers} />

        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-zinc-200 p-1 rounded-xl w-fit mb-6 flex-wrap">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-[13px] font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                tab === t.id
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${t.icon} text-sm`} />
              </div>
              {t.label}
            </button>
          ))}
        </div>

        {/* Feedback */}
        {userMsg && (
          <div className="mb-5 flex items-center gap-2.5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />
            </div>
            <p className="text-[13px] text-emerald-700">{userMsg}</p>
          </div>
        )}
        {userError && (
          <div className="mb-5 flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-red-500 text-sm" />
            </div>
            <p className="text-[13px] text-red-700">{userError}</p>
          </div>
        )}

        {/* Lista de utilizadores */}
        {tab === 'utilizadores' && (() => {
          // Calcular utilizadores filtrados
          const porModulo = filtroModulo === 'todos'
            ? users
            : filtroModulo === 'sem_modulo'
              ? users.filter((u) => !u.municipio_id)
              : users.filter((u) => u.municipio_id === filtroModulo);

          const termoPesquisa = pesquisa.trim().toLowerCase();
          const utilizadoresFiltrados = termoPesquisa
            ? porModulo.filter((u) =>
                (u.full_name ?? '').toLowerCase().includes(termoPesquisa) ||
                (u.email ?? '').toLowerCase().includes(termoPesquisa)
              )
            : porModulo;

          return (
          <div className="space-y-4">
            {/* Barra de filtro por módulo */}
            <div className="bg-white rounded-2xl border border-zinc-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 flex items-center justify-center text-zinc-400">
                  <i className="ri-filter-3-line text-sm" />
                </div>
                <span className="text-[12px] font-semibold text-zinc-500 uppercase tracking-wider">Filtrar por módulo</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFiltroModulo('todos')}
                  className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold border transition-all cursor-pointer ${
                    filtroModulo === 'todos'
                      ? 'bg-zinc-900 text-white border-zinc-900'
                      : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                  }`}
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-group-line text-xs" />
                  </div>
                  Todos
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    filtroModulo === 'todos' ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-500'
                  }`}>{allUsers.length}</span>
                </button>

                {municipios.map((m) => {
                  const count = users.filter((u) => u.municipio_id === m.id).length;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setFiltroModulo(m.id)}
                      className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold border transition-all cursor-pointer ${
                        filtroModulo === m.id
                          ? 'bg-zinc-900 text-white border-zinc-900'
                          : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                      }`}
                    >
                      <div className={`w-5 h-5 flex items-center justify-center rounded-md flex-shrink-0 text-[9px] font-bold ${
                        filtroModulo === m.id ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-600'
                      }`}>
                        {m.sigla ? m.sigla.slice(0, 2).toUpperCase() : <i className="ri-building-4-line" />}
                      </div>
                      {m.nome}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        filtroModulo === m.id ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-500'
                      }`}>{allUsers.filter((u) => u.municipio_id === m.id).length}</span>
                    </button>
                  );
                })}

                {users.filter((u) => !u.municipio_id).length > 0 && (
                  <button
                    onClick={() => setFiltroModulo('sem_modulo')}
                    className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold border transition-all cursor-pointer ${
                      filtroModulo === 'sem_modulo'
                        ? 'bg-zinc-900 text-white border-zinc-900'
                        : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'
                    }`}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-question-line text-xs" />
                    </div>
                    Sem módulo
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      filtroModulo === 'sem_modulo' ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-500'
                    }`}>{users.filter((u) => !u.municipio_id).length}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Barra de pesquisa */}
            <div className="bg-white rounded-2xl border border-zinc-100 p-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <div className="w-4 h-4 flex items-center justify-center text-zinc-400">
                    <i className="ri-search-2-line text-sm" />
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Pesquisar por nome ou email..."
                  value={pesquisa}
                  onChange={(e) => setPesquisa(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors"
                />
                {pesquisa && (
                  <button
                    onClick={() => setPesquisa('')}
                    className="absolute inset-y-0 right-3 flex items-center text-zinc-400 hover:text-zinc-600 cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-close-line text-sm" />
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Lista */}
            <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                  <i className="ri-team-line text-zinc-600 text-sm" />
                </div>
                <div>
                  <h2 className="text-[14px] font-semibold text-zinc-800">
                    Utilizadores
                    {filtroModulo !== 'todos' && (
                      <span className="ml-2 text-[11px] font-semibold text-zinc-400 bg-zinc-100 px-2 py-0.5 rounded-full">
                        {filtroModulo === 'sem_modulo' ? 'Sem módulo' : municipios.find((m) => m.id === filtroModulo)?.nome ?? ''}
                      </span>
                    )}
                  </h2>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    {utilizadoresFiltrados.length} utilizador{utilizadoresFiltrados.length !== 1 ? 'es' : ''}
                    {pesquisa ? ` encontrado${utilizadoresFiltrados.length !== 1 ? 's' : ''}` : filtroModulo !== 'todos' ? ' neste módulo' : ' no total'}
                  </p>
                </div>
              </div>
              {isSuperAdmin && (
                <button
                  onClick={() => setShowCreate(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-add-line text-sm" />
                  </div>
                  Novo Utilizador
                </button>
              )}
            </div>

            {loadingUsers ? (
              <div className="flex items-center justify-center py-14">
                <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
              </div>
            ) : utilizadoresFiltrados.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-14">
                <div className="w-10 h-10 flex items-center justify-center bg-zinc-100 rounded-full">
                  <i className="ri-user-search-line text-zinc-400 text-lg" />
                </div>
                <p className="text-[13px] text-zinc-400">
                  {pesquisa ? `Nenhum resultado para "${pesquisa}"` : 'Nenhum utilizador neste módulo'}
                </p>
                {pesquisa && (
                  <button onClick={() => setPesquisa('')} className="text-[12px] text-zinc-500 underline cursor-pointer hover:text-zinc-700">
                    Limpar pesquisa
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-zinc-50">
                {utilizadoresFiltrados.map((u) => {{
                  const moduloNome = municipios.find((m) => m.id === u.municipio_id)?.nome;
                  return (
                  <div key={u.id} className="flex items-center justify-between px-6 py-4 hover:bg-zinc-50/60 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-full flex-shrink-0">
                        <span className="text-[13px] font-bold text-zinc-600">
                          {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-zinc-800">{u.full_name || '—'}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <p className="text-[11px] text-zinc-400">{u.email}</p>
                          {moduloNome && filtroModulo === 'todos' && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded-full">
                              {moduloNome}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* Último acesso */}
                      <div className="hidden sm:flex items-center gap-1.5 text-zinc-400" title={(u as Profile & { last_sign_in_at?: string }).last_sign_in_at ? new Date((u as Profile & { last_sign_in_at?: string }).last_sign_in_at!).toLocaleString('pt-PT') : 'Nunca acedeu'}>
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-time-line text-xs" />
                        </div>
                        <span className="text-[11px]">{formatLastAccess((u as Profile & { last_sign_in_at?: string }).last_sign_in_at)}</span>
                      </div>
                      <span className={`px-2.5 py-1 text-[11px] font-semibold rounded-full ${roleBadge[u.role ?? 'viewer'] ?? roleBadge['viewer']}`}>
                        {roleLabel[u.role ?? 'viewer'] ?? u.role}
                      </span>
                      {u.id === profile?.id && (
                        <span className="px-2 py-0.5 text-[11px] text-zinc-400 bg-zinc-50 border border-zinc-200 rounded-full">Tu</span>
                      )}
                      {isSuperAdmin && u.id !== profile?.id && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditUser(u)}
                            className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer" title="Editar">
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button onClick={() => setChangePassUser(u)}
                            className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer" title="Alterar password">
                            <i className="ri-key-line text-sm" />
                          </button>
                          <button onClick={() => setDeleteUser(u)}
                            className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer" title="Remover">
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                }})
              }
              </div>
            )}
            </div>
          </div>
          );
        })()}

        {tab === 'municipios'    && <MunicipiosTab />}
        {tab === 'unidades'      && <UnidadesTab />}
        {tab === 'carreiras'     && <CarreirasTab />}
        {tab === 'alteracoes'    && <AlteracoesTab />}
        {tab === 'configuracoes'          && <SettingsTab />}
        {tab === 'configuracoes_formacao'  && <SettingsFormacaoTab />}
        {tab === 'aparencia'               && <AppearanceTab />}
        {tab === 'aparencia_formacao'      && <AppearanceFormacaoTab />}
        {tab === 'tarefas'      && <CronJobsTab />}
        {tab === 'templates'    && <EmailTemplatesTab />}
        {tab === 'feriados'     && <FeriadosTab />}
        {tab === 'dados_mestre' && <DadosMestreTab />}
        {tab === 'diagnostico'  && <DiagnosticoTab />}
        {tab === 'logs'         && <LogsTab />}
      </div>

      {showCreate    && <UserFormModal mode="create" isSuperAdmin={isSuperAdmin} onClose={() => setShowCreate(false)} onSave={handleCreate} />}
      {editUser      && <UserFormModal mode="edit" isSuperAdmin={isSuperAdmin} initialData={editUser} onClose={() => setEditUser(null)} onSave={handleEdit} />}
      {deleteUser    && <DeleteUserModal userName={deleteUser.full_name || deleteUser.email} onClose={() => setDeleteUser(null)} onConfirm={handleDelete} />}
      {changePassUser && <ChangePasswordModal userName={changePassUser.full_name || changePassUser.email} onClose={() => setChangePassUser(null)} onSave={handleChangePassword} />}
    </div>
  );
}
